import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Layers, Plus, Star, Trash2, Save, Upload as UploadIcon, FileText, Pencil, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PreviewCanvas from "@/components/PreviewCanvas";

const SAMPLE_DATA = {
  name: "Wajid",
  title: "Oracle Fusion HCM Consultant",
  contact: { email: "wajid@example.com", phone: "+971 50 000 0000", location: "Dubai, UAE", linkedin: "linkedin.com/in/wajid" },
  summary: "Experienced Oracle Fusion HCM Consultant with deep expertise in Core HR, Payroll, Absence Management, Performance Management, and Oracle Recruiting Cloud. Proven track record delivering end-to-end implementations across large multinational environments.",
  experiences: [
    { role: "Oracle Fusion HCM Consultant", company: "SAAIT, Dubai", location: "Remote – Qatar", start_date: "Jan 2025", end_date: "Present", bullets: ["Delivered multiple Oracle Fusion HCM implementations across diverse industries.", "Designed payroll rules and Fast Formulas, significantly improving accuracy.", "Implemented AI-powered HCM Agent reducing HR support queries by 30%."] },
    { role: "Payroll Manager & Support Consultant", company: "NAFFCO", location: "Qatar", start_date: "Jun 2021", end_date: "Jan 2025", bullets: ["Managed end-to-end payroll for 2,000+ employees with full regional compliance.", "Achieved 100% WPS compliance with zero audit penalties."] },
  ],
  earlier_roles: [
    { role: "HRMS Functional Consultant", company: "Coremind Technologies", duration: "2009 – 2010" },
    { role: "ERP HRMS Consultant", company: "Metamorphosis Technologies", duration: "2007 – 2009" },
  ],
  education: [
    { degree: "MBA", institution: "Alagappa University", duration: "2010" },
    { degree: "MCA", institution: "Bharathidasan University", duration: "2007" },
  ],
  competencies: ["Core HR", "Payroll", "Absence Mgmt", "ORC", "Fast Formulas", "HDL Migration", "OTBI", "BI Publisher", "Workflow Config", "Compliance"],
  certifications: [{ name: "Oracle Cloud AI Foundations Associate", issuer: "Oracle", year: "2024" }, { name: "Oracle Fusion HCM Process Essentials", issuer: "Oracle", year: "2023" }],
  languages: [{ name: "English", proficiency: "Fluent" }, { name: "Arabic", proficiency: "Conversational" }, { name: "Hindi", proficiency: "Fluent" }, { name: "Urdu", proficiency: "Native" }, { name: "Tamil", proficiency: "Native" }],
  highlights: [{ title: "18+ Years Experience", description: "Across Oracle EBS & Fusion Cloud ecosystems for 2,000-5,000+ employee organizations." }, { title: "AI HR Automation", description: "Reduced manual effort and HR support queries by 30%." }],
  additional_info: ["Nationality: Indian"],
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Templates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template object being edited (null for "new")
  const [open, setOpen] = useState(false);
  const jsonFileRef = useRef(null);
  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);

  const refresh = async () => { const r = await api.get("/templates"); setTemplates(r.data); };
  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setEditing({
      name: "Untitled Template",
      description: "",
      accent_color: "#1E5BFF",
      sidebar_bg: "#F4F6FA",
      sidebar_text: "#0F172A",
      page_bg: "#FFFFFF",
      text_color: "#0F172A",
      muted_color: "#64748B",
      header_style: "underline",
      font_heading: "Manrope",
      font_body: "Inter",
      left_ratio: 0.66,
      page_size: "A4",
      custom_width_mm: 210,
      custom_height_mm: 297,
      density: "normal",
      font_scale: 1.0,
      base_pt: 10,
      page_margin_mm: 12,
      hero_enabled: false,
      hero_style: "gradient",
      hero_bg_color: "#0B2A6B",
      hero_company_name: "",
      timeline: true,
      is_default: false,
    });
    setOpen(true);
  };

  const startEdit = (t) => { setEditing({ ...t }); setOpen(true); };

  const exportJson = (t, e) => {
    if (e) e.stopPropagation();
    const { id, created_at, updated_at, ...clean } = t;  // eslint-disable-line no-unused-vars
    const blob = new Blob([JSON.stringify(clean, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(t.name || "template").replace(/[^a-z0-9]+/gi, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      delete parsed.id;  // force new id
      parsed.name = parsed.name ? `${parsed.name} (imported)` : "Imported template";
      parsed.is_default = false;
      await api.post("/templates", parsed);
      toast.success("Template imported");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid JSON or upload failed");
    }
    if (jsonFileRef.current) jsonFileRef.current.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold">Engine</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Templates</h1>
          <p className="text-muted-foreground mt-1 text-sm">Design reusable resume layouts the AI maps every uploaded resume into.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <input ref={jsonFileRef} type="file" accept=".json" className="hidden" onChange={importJson} data-testid="template-import-json-input" />
            <Button variant="outline" onClick={() => jsonFileRef.current?.click()} className="gap-2 lift" data-testid="template-import-json-button"><UploadIcon className="size-4" /> Import JSON</Button>
            <Button onClick={startNew} className="gap-2 lift" data-testid="template-new-button"><Plus className="size-4" /> New template</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <Card key={t.id} className="border-border lift hover:border-primary/40 cursor-pointer overflow-hidden" data-testid={`template-${t.id}`} onClick={() => isAdmin && startEdit(t)}>
            <div className="h-40 grid grid-cols-3 overflow-hidden border-b border-border">
              <div className="col-span-2 p-3" style={{ background: t.page_bg }}>
                <div className="h-3 w-16 rounded" style={{ background: t.text_color, opacity: 0.85 }} />
                <div className="h-2 w-24 rounded mt-1.5" style={{ background: t.accent_color }} />
                <div className="h-1 w-full rounded mt-3" style={{ background: t.muted_color, opacity: 0.3 }} />
                <div className="h-2 w-12 rounded mt-3" style={{ background: t.accent_color }} />
                <div className="h-1.5 rounded mt-1" style={{ background: t.muted_color, opacity: 0.4 }} />
                <div className="h-1.5 rounded mt-1 w-3/4" style={{ background: t.muted_color, opacity: 0.4 }} />
              </div>
              <div className="col-span-1 p-2" style={{ background: t.sidebar_bg }}>
                <div className="h-1.5 w-10 rounded" style={{ background: t.accent_color }} />
                <div className="flex flex-wrap gap-1 mt-2">
                  {[...Array(5)].map((_, i) => <span key={i} className="h-2 w-6 rounded-full bg-white border border-border" />)}
                </div>
                <div className="h-1.5 w-8 rounded mt-3" style={{ background: t.accent_color }} />
                <div className="flex gap-0.5 mt-2">{[0,1,2,3,4].map((n) => <span key={n} className="size-1 rounded-full" style={{ background: n < 4 ? t.accent_color : "#CBD5E1" }} />)}</div>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="display font-bold truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{t.font_heading} · {t.header_style}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {t.is_default && <span className="chip flex items-center gap-1"><Star className="size-3" /> Default</span>}
                  {isAdmin && (
                    <Button size="icon" variant="ghost" onClick={(e) => exportJson(t, e)} title="Export JSON" data-testid={`template-export-${t.id}`}>
                      <FileText className="size-4" />
                    </Button>
                  )}
                  {isAdmin && !t.is_default && (
                    <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); del(t.id); }} className="text-destructive" data-testid={`template-delete-${t.id}`}>
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {open && editing && (
        <TemplateEditor
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TemplateEditor({ initial, onClose }) {
  const [t, setT] = useState(initial);
  const [saving, setSaving] = useState(false);
  const refRef = useRef(null);
  const heroRef = useRef(null);
  const isExisting = !!initial.id;

  const set = (patch) => setT((x) => ({ ...x, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      if (isExisting) {
        await api.put(`/templates/${t.id}`, t);
        toast.success("Template updated");
      } else {
        const r = await api.post("/templates", t);
        // Update local id so subsequent ref upload works
        Object.assign(t, r.data);
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const uploadRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!t.id) {
      // create first
      try {
        const r = await api.post("/templates", t);
        setT({ ...t, id: r.data.id });
        await doUpload(r.data.id, file);
      } catch (e2) { toast.error("Could not create template"); }
    } else {
      await doUpload(t.id, file);
    }
  };

  const doUpload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/templates/${id}/upload-reference`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setT({ ...t, ...r.data });
      toast.success("Reference uploaded");
    } catch { toast.error("Upload failed"); }
    if (refRef.current) refRef.current.value = "";
  };

  const uploadHeroLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let tid = t.id;
    if (!tid) {
      // Create the template first so we have an id
      try {
        const r = await api.post("/templates", t);
        tid = r.data.id;
        setT({ ...t, id: tid });
      } catch { toast.error("Could not create template"); return; }
    }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/templates/${tid}/upload-hero-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setT({ ...t, ...r.data, id: tid });
      toast.success("Hero logo uploaded");
    } catch { toast.error("Upload failed"); }
    if (heroRef.current) heroRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm overflow-y-auto" data-testid="template-editor-modal">
      <div className="min-h-screen flex flex-col">
        <div className="border-b border-border bg-card flex items-center justify-between px-4 md:px-6 py-3 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Layers className="size-5 text-primary" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{isExisting ? "Editing" : "New template"}</div>
              <Input value={t.name} onChange={(e) => set({ name: e.target.value })} className="h-8 border-0 px-0 font-bold display text-lg focus-visible:ring-0 min-w-[260px]" data-testid="template-name-input" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="template-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2 lift" data-testid="template-save-button"><Save className="size-4" /> {saving ? "Saving…" : "Save template"}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 min-h-0">
          {/* Editor */}
          <div className="overflow-y-auto p-5 md:p-7 border-r border-border space-y-4">
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="display font-bold">Basics</div>
                <Field label="Description">
                  <Textarea rows={2} value={t.description} onChange={(e) => set({ description: e.target.value })} data-testid="template-desc-input" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Page size">
                    <Select value="A4" disabled>
                      <SelectTrigger data-testid="template-page-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A4">A4 (210 × 297 mm) — Standard</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">All resumes are A4 — ensures consistent PDF + DOCX across recruiters and clients.</p>
                  </Field>
                  <Field label="Density">
                    <Select value={t.density || "normal"} onValueChange={(v) => set({ density: v })}>
                      <SelectTrigger data-testid="template-density"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compact">Compact</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="spacious">Spacious</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {/* Custom page size removed — locked to A4 for consistency */}
                  <Field label="Base body font (pt)">
                    <Select value={String(t.base_pt || 10)} onValueChange={(v) => set({ base_pt: Number(v) })}>
                      <SelectTrigger data-testid="template-base-pt"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[8, 9, 10, 11, 12, 13].map((n) => <SelectItem key={n} value={String(n)}>{n}pt {n === 10 ? "· default" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Heading font">
                    <Select value={t.font_heading} onValueChange={(v) => set({ font_heading: v })}>
                      <SelectTrigger data-testid="template-heading-font"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Manrope", "Inter", "Outfit", "Plus Jakarta Sans", "Space Grotesk"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Body font">
                    <Select value={t.font_body} onValueChange={(v) => set({ font_body: v })}>
                      <SelectTrigger data-testid="template-body-font"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Inter", "Manrope", "Source Sans 3", "DM Sans", "Lato"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Section header style">
                    <Select value={t.header_style} onValueChange={(v) => set({ header_style: v })}>
                      <SelectTrigger data-testid="template-header-style"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="underline">Underline (default)</SelectItem>
                        <SelectItem value="block">Solid block</SelectItem>
                        <SelectItem value="minimal">Minimal</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={`Left column ratio · ${Math.round(t.left_ratio * 100)}%${t.left_ratio >= 0.98 ? " (single-column)" : ""}`}>
                    <Slider value={[t.left_ratio * 100]} min={55} max={100} step={1} onValueChange={(v) => set({ left_ratio: v[0] / 100 })} data-testid="template-ratio-slider" />
                  </Field>
                  <Field label={`Page margin · ${Math.round(t.page_margin_mm ?? 12)}mm`}>
                    <Slider value={[t.page_margin_mm ?? 12]} min={0} max={25} step={1} onValueChange={(v) => set({ page_margin_mm: v[0] })} data-testid="template-page-margin" />
                  </Field>
                  <Field label="Target pages">
                    <Select value={String(t.target_pages ?? 0)} onValueChange={(v) => set({ target_pages: Number(v) })}>
                      <SelectTrigger data-testid="template-target-pages"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Unlimited</SelectItem>
                        <SelectItem value="1">1 page</SelectItem>
                        <SelectItem value="2">2 pages</SelectItem>
                        <SelectItem value="3">3 pages</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.timeline} onCheckedChange={(v) => set({ timeline: v })} data-testid="template-timeline-switch" />
                    <span className="text-sm">Show experience timeline</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.show_candidate_photo} onCheckedChange={(v) => set({ show_candidate_photo: v })} data-testid="template-photo-switch" />
                    <span className="text-sm">Show candidate photo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.show_footer_logo} onCheckedChange={(v) => set({ show_footer_logo: v })} data-testid="template-footer-logo-switch" />
                    <span className="text-sm">Brand logo footer (PDF)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.is_default} onCheckedChange={(v) => set({ is_default: v })} data-testid="template-default-switch" />
                    <span className="text-sm">Set as default for new uploads</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Multi-page settings */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="display font-bold">Multi-page settings <span className="text-xs text-muted-foreground font-normal">(header / footer repeat on pages 2+)</span></div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.repeat_header} onCheckedChange={(v) => set({ repeat_header: v })} data-testid="template-repeat-header-switch" />
                    <span className="text-sm">Repeat header on pages 2+</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.repeat_footer} onCheckedChange={(v) => set({ repeat_footer: v })} data-testid="template-repeat-footer-switch" />
                    <span className="text-sm">Repeat footer on every page</span>
                  </div>
                </div>
                {t.repeat_header && (
                  <Field label="Header text (leave empty to use candidate name)">
                    <Input value={t.header_text || ""} onChange={(e) => set({ header_text: e.target.value })} placeholder="e.g. CONFIDENTIAL · Submitted via Quantira" data-testid="template-header-text" />
                  </Field>
                )}
                {t.repeat_footer && (
                  <Field label="Footer text · placeholders: {name}, {title}, {page}, {pages}">
                    <Input value={t.footer_text || ""} onChange={(e) => set({ footer_text: e.target.value })} placeholder="e.g. {name} · {title} · Page {page} of {pages}" data-testid="template-footer-text" />
                  </Field>
                )}
              </CardContent>
            </Card>

            {/* Hero banner card */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="display font-bold">Hero banner <span className="text-xs text-muted-foreground font-normal">(top of resume)</span></div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!t.hero_enabled} onCheckedChange={(v) => set({ hero_enabled: v })} data-testid="template-hero-switch" />
                    <span className="text-xs font-semibold">{t.hero_enabled ? "Enabled" : "Disabled"}</span>
                  </div>
                </div>
                {t.hero_enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Hero style">
                        <Select value={t.hero_style || "gradient"} onValueChange={(v) => set({ hero_style: v })}>
                          <SelectTrigger data-testid="template-hero-style"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gradient">Dark gradient (premium)</SelectItem>
                            <SelectItem value="solid">Solid colour</SelectItem>
                            <SelectItem value="accent_bar">Accent-colour bar</SelectItem>
                            <SelectItem value="minimal">Minimal line (white bg)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <ColorField label="Hero background" value={t.hero_bg_color || "#0B2A6B"} onChange={(v) => set({ hero_bg_color: v })} testid="template-hero-color" />
                      <Field label="Company / Agency name (shown above the name)">
                        <Input value={t.hero_company_name || ""} onChange={(e) => set({ hero_company_name: e.target.value })} data-testid="template-hero-company" placeholder="e.g. Quantira Technologies" />
                      </Field>
                      <div>
                        <Label className="text-xs font-semibold">Hero logo</Label>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="size-12 rounded-md border border-border bg-muted grid place-items-center overflow-hidden shrink-0">
                            {t.hero_logo_url
                              ? <img src={`${BACKEND_URL}${t.hero_logo_url}`} alt="logo" className="max-w-full max-h-full object-contain" />
                              : <ImageIcon className="size-4 text-muted-foreground" />}
                          </div>
                          <input ref={heroRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={uploadHeroLogo} className="hidden" data-testid="template-hero-logo-input" />
                          <Button size="sm" variant="outline" onClick={() => heroRef.current?.click()} className="gap-2 lift" data-testid="template-hero-logo-upload">
                            <UploadIcon className="size-4" /> {t.hero_logo_url ? "Replace" : "Upload"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="display font-bold">Colors</div>
                <div className="grid grid-cols-2 gap-3">
                  <ColorField label="Accent" value={t.accent_color} onChange={(v) => set({ accent_color: v })} testid="color-accent" />
                  <ColorField label="Primary text" value={t.text_color} onChange={(v) => set({ text_color: v })} testid="color-text" />
                  <ColorField label="Muted text" value={t.muted_color} onChange={(v) => set({ muted_color: v })} testid="color-muted" />
                  <ColorField label="Page background" value={t.page_bg} onChange={(v) => set({ page_bg: v })} testid="color-pagebg" />
                  <ColorField label="Sidebar background" value={t.sidebar_bg} onChange={(v) => set({ sidebar_bg: v })} testid="color-sidebar" />
                  <ColorField label="Sidebar text" value={t.sidebar_text} onChange={(v) => set({ sidebar_text: v })} testid="color-sidebartext" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-4">
                <div className="display font-bold mb-2">Reference design (optional)</div>
                <div className="text-xs text-muted-foreground mb-3">Upload a PDF or image of an existing resume to keep alongside this template for visual reference.</div>
                <input ref={refRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadRef} className="hidden" data-testid="template-ref-input" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => refRef.current?.click()} className="gap-2 lift" data-testid="template-ref-upload-button">
                    <UploadIcon className="size-4" /> Upload reference
                  </Button>
                  {t.reference_url && (
                    <a href={`${BACKEND_URL}${t.reference_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                      <FileText className="size-4" /> View reference
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live preview */}
          <div className="overflow-y-auto bg-secondary/30 grid-bg p-5 md:p-7">
            <div className="max-w-[640px] mx-auto">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3 flex items-center gap-2">
                <Pencil className="size-3.5" /> Live preview · {t.page_size || "A4"} · {Math.round(Number(t.base_pt || 10))}pt
              </div>
              <PreviewCanvas data={SAMPLE_DATA} style={t} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="size-9 rounded border border-border cursor-pointer" data-testid={`${testid}-picker`} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" data-testid={`${testid}-text`} />
      </div>
    </div>
  );
}
